import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'

import appointmentService from '../../../services/appointment.service'
import ReminderHistory from './ReminderHistory'

/**
 * "When is my car booked in?" — US-37724.
 *
 * Until this shipped a client was NEVER told the date their work was scheduled for. Estimate,
 * WorkOrder and Invoice have all carried an `event` ref for years and no portal read path ever
 * used it.
 *
 * TIMEZONE AND ALL-DAY. Both rules come from the API contract in AB#37632 and both have already
 * caused visible bugs on the admin side:
 *
 *  - `event.timeZone` is usually an IANA NAME ('America/Chicago'), not an offset. Passing it to
 *    moment's utcOffset silently falls back to the viewer's zone and every event renders an
 *    hour out. It has to go through Intl.
 *  - An all-day event is anchored at noon UTC on BOTH ends and carries timeZone '+00:00', which
 *    means "no offset applies" rather than UTC-the-place. Its date parts are read in UTC and it
 *    never shows a clock time.
 */

const TERMINAL = ['cancelled', 'noShow']

/** Minutes east of UTC for an IANA zone at a given instant. */
const zoneOffsetMinutes = (zone, instant) => {
  if (!zone) return null
  // An offset string is already an offset; only a name needs resolving.
  const asOffset = /^[+-]\d{2}:?\d{2}$/.exec(zone)
  if (asOffset) {
    const sign = zone[0] === '-' ? -1 : 1
    const [h, m] = zone.slice(1).replace(':', ' ').split(' ')
    return sign * (parseInt(h, 10) * 60 + parseInt(m || '0', 10))
  }
  try {
    const dtf = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      hour12: false,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    })
    const parts = dtf.formatToParts(instant).reduce((acc, p) => {
      acc[p.type] = p.value
      return acc
    }, {})
    const asUTC = Date.UTC(
      Number(parts.year), Number(parts.month) - 1, Number(parts.day),
      Number(parts.hour) % 24, Number(parts.minute), Number(parts.second),
    )
    return Math.round((asUTC - instant.getTime()) / 60000)
  } catch (e) {
    // An unknown zone must not take the panel down with it.
    return null
  }
}

const formatWhen = appointment => {
  if (!appointment || !appointment.startTime) return ''
  const start = new Date(appointment.startTime)

  if (appointment.allDay) {
    // UTC date parts, never a clock time — see the note above.
    const end = appointment.endTime ? new Date(appointment.endTime) : start
    const from = moment.utc(start).format('dddd, MMMM D')
    const to = moment.utc(end).format('dddd, MMMM D')
    return from === to ? `${from} · All day` : `${from} — ${to} · All day`
  }

  const offset = zoneOffsetMinutes(appointment.timeZone, start)
  const at = offset === null ? moment(start) : moment(start).utcOffset(offset)
  return at.format('dddd, MMMM D · h:mm A')
}

const STATE_COPY = {
  unconfirmed: { label: 'Awaiting your confirmation', tone: 'pending' },
  confirmed: { label: 'Confirmed', tone: 'ok' },
  declined: { label: 'You declined this time', tone: 'warn' },
  arrived: { label: 'Vehicle arrived', tone: 'ok' },
  noShow: { label: 'Missed', tone: 'warn' },
  cancelled: { label: 'Cancelled', tone: 'warn' },
}

const AppointmentPanel = ({ eventId, enabled }) => {
  const [appointment, setAppointment] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!enabled || !eventId) return
    let live = true
    appointmentService()
      .get(eventId)
      .then(res => {
        if (live) setAppointment((res && res.data) || null)
      })
      // A client with no appointment, or one they do not own, simply sees nothing.
      .catch(() => { if (live) setAppointment(null) })
    return () => { live = false }
  }, [eventId, enabled])

  if (!enabled || !eventId || !appointment) return null

  const state = appointment.bookingState || 'unconfirmed'
  const copy = STATE_COPY[state] || STATE_COPY.unconfirmed
  const canRespond = TERMINAL.indexOf(state) === -1

  const respond = next => {
    setBusy(true)
    setError('')
    appointmentService()
      .setBookingState(eventId, next)
      .then(res => setAppointment((res && res.data) || appointment))
      .catch(() => setError('That did not go through. Please try again, or call the shop.'))
      .then(() => setBusy(false))
  }

  const techs = (appointment.users || []).map(u => u.name).filter(Boolean)

  return (
    <div className={`appointment-panel appointment-panel--${copy.tone}`}>
      <div className='appointment-panel__head'>
        <span className='appointment-panel__label'>APPOINTMENT</span>
        <span className='appointment-panel__state'>{copy.label}</span>
      </div>

      <div className='appointment-panel__when'>{formatWhen(appointment)}</div>

      {/* The stage the work is actually in. Name and colour only — the tracking subdocument
          also carries timers, assignees and an internal activity log, none of which is the
          client's business. */}
      {appointment.workflow && appointment.workflow.name && (
        <div className='appointment-panel__stage'>
          <span
            className='appointment-panel__stage-dot'
            style={{ backgroundColor: appointment.workflow.color || '#307FE2' }}
          />
          {appointment.workflow.name}
        </div>
      )}

      {techs.length > 0 && (
        <div className='appointment-panel__techs'>
          {techs.length === 1 ? techs[0] : `${techs.join(', ')}`}
        </div>
      )}

      {error && <div className='appointment-panel__error'>{error}</div>}

      <ReminderHistory reminders={appointment.reminders} shop={appointment.shop} />

      {canRespond && (
        <div className='appointment-panel__actions'>
          <button
            type='button'
            className='btn btn-primary'
            disabled={busy || state === 'confirmed'}
            onClick={() => respond('confirmed')}
          >
            {state === 'confirmed' ? 'CONFIRMED' : 'CONFIRM'}
          </button>
          <button
            type='button'
            className='btn btn-link'
            disabled={busy || state === 'declined'}
            onClick={() => respond('declined')}
          >
            {state === 'declined' ? 'DECLINED' : "THIS TIME DOESN'T WORK"}
          </button>
        </div>
      )}
    </div>
  )
}

AppointmentPanel.propTypes = {
  // The `event` ref already carried by the estimate, work order or invoice.
  eventId: PropTypes.string,
  /**
   * US-38458. Whether the client-facing scheduling surface is offered at all.
   *
   * REQUIRED, with no default, on purpose. A default would decide this for a call site that
   * forgot to, and the two kinds of call site want opposite answers: a document page reads
   * `featureFlags.schedulingWeb` from the login response, while the confirm-link page has no
   * session and therefore no flags to read, so it passes `true` and leans on the core API's
   * gate. Making it explicit is what stops the next surface inheriting the wrong one silently.
   */
  enabled: PropTypes.bool.isRequired,
}

AppointmentPanel.defaultProps = {
  eventId: null,
}

export default AppointmentPanel
